// Components/DateRangePicker.js
import React, {useEffect, useRef, useState} from 'react';
import {Calendar} from 'lucide-react';

const DateRangePicker = ({startDate, endDate, onChange, placeholder, className}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [localStartDate, setLocalStartDate] = useState(startDate);
    const [localEndDate, setLocalEndDate] = useState(endDate);
    const pickerRef = useRef(null);

    // Format date for display
    const formatDate = (date) => {
        if (!date) return '';
        return date instanceof Date
            ? date.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'})
            : new Date(date).toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});
    };

    // Handle display text for the picker
    const getDisplayText = () => {
        if (!localStartDate && !localEndDate) return placeholder || 'Select date range';
        if (localStartDate && !localEndDate) return `From ${formatDate(localStartDate)}`;
        if (!localStartDate && localEndDate) return `Until ${formatDate(localEndDate)}`;
        return `${formatDate(localStartDate)} - ${formatDate(localEndDate)}`;
    };

    // Handle apply button
    const handleApply = () => {
        onChange(localStartDate, localEndDate);
        setIsOpen(false);
    };

    // Handle clear button
    const handleClear = () => {
        setLocalStartDate(null);
        setLocalEndDate(null);
        onChange(null, null);
        setIsOpen(false);
    };

    // Handle outside click to close the picker
    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isOpen]);

    // Sync local state with props
    useEffect(() => {
        setLocalStartDate(startDate);
        setLocalEndDate(endDate);
    }, [startDate, endDate]);

    return (
        <div className="relative" ref={pickerRef}>
            <div
                className={`flex items-center cursor-pointer ${className}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <Calendar size={20} className="mr-2 text-gray-500"/>
                <span className="flex-1">{getDisplayText()}</span>
            </div>

            {isOpen && (
                <div className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-72">
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={localStartDate instanceof Date ? localStartDate.toISOString().slice(0, 10) : ''}
                                onChange={(e) => {
                                    const date = e.target.value ? new Date(e.target.value) : null;
                                    setLocalStartDate(date);
                                }}
                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date
                            </label>
                            <input
                                type="date"
                                value={localEndDate instanceof Date ? localEndDate.toISOString().slice(0, 10) : ''}
                                onChange={(e) => {
                                    const date = e.target.value ? new Date(e.target.value) : null;
                                    setLocalEndDate(date);
                                }}
                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                        </div>

                        {/* Preset ranges */}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <button
                                onClick={() => {
                                    const end = new Date();
                                    const start = new Date();
                                    start.setDate(start.getDate() - 7);
                                    setLocalStartDate(start);
                                    setLocalEndDate(end);
                                }}
                                className="p-1 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                            >
                                Last 7 days
                            </button>
                            <button
                                onClick={() => {
                                    const end = new Date();
                                    const start = new Date();
                                    start.setDate(start.getDate() - 30);
                                    setLocalStartDate(start);
                                    setLocalEndDate(end);
                                }}
                                className="p-1 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                            >
                                Last 30 days
                            </button>
                            <button
                                onClick={() => {
                                    const end = new Date();
                                    const start = new Date();
                                    start.setDate(start.getDate() - 90);
                                    setLocalStartDate(start);
                                    setLocalEndDate(end);
                                }}
                                className="p-1 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                            >
                                Last 90 days
                            </button>
                            <button
                                onClick={() => {
                                    const today = new Date();
                                    const start = new Date(today.getFullYear(), 0, 1); // January 1st of current year
                                    setLocalStartDate(start);
                                    setLocalEndDate(today);
                                }}
                                className="p-1 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                            >
                                Year to date
                            </button>
                        </div>

                        <div className="flex justify-between mt-4">
                            <button
                                onClick={handleClear}
                                className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm"
                            >
                                Clear
                            </button>
                            <button
                                onClick={handleApply}
                                className="px-3 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors text-sm"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;